import React, { useEffect, useState } from 'react';
import { axiosKK } from '../../../services/networkRequest';
import { ServiceAreaDTO } from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import {
  AddButton,
  AdminNotice,
  DataTable,
  DataTableColumn,
  InlineEditor,
  InlineEditorField,
} from '../../../components/admin/Shared';
import { DEFAULT_SERVICE_AREAS } from './defaults';

interface Props {
  partnerId: number;
  /**
   * When true, hide the AddButton + per-row Edit/Delete affordances and
   * the suggested-defaults panel. The DataTable still renders.
   * Default: false (admin behaviour).
   */
  readOnly?: boolean;
}

interface ServiceAreaFormValues extends Record<string, unknown> {
  postcodePrefix: string;
  priority: string | number;
}

const EDITOR_FIELDS: InlineEditorField[] = [
  {
    name: 'postcodePrefix',
    label: 'Postcode prefix',
    type: 'text',
    required: true,
    placeholder: 'e.g. SW1',
  },
  {
    name: 'priority',
    label: 'Priority',
    type: 'number',
  },
];

export default function ServiceAreasTab({ partnerId, readOnly = false }: Props) {
  const [areas, setAreas] = useState<ServiceAreaDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [netError, setNetError] = useState<string>();

  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [editingSeed, setEditingSeed] = useState<Partial<ServiceAreaFormValues>>({});
  const [submitting, setSubmitting] = useState(false);
  const [saveBanner, setSaveBanner] = useState<string>();

  const refresh = React.useCallback(() => {
    setLoading(true);
    axiosKK
      .get<{ serviceAreas: ServiceAreaDTO[] }>(`/admin/partners/${partnerId}/service-areas`)
      .then(({ data }) => setAreas(data.serviceAreas || []))
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setLoading(false));
  }, [partnerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setEditingId('new');
    setEditingSeed({ postcodePrefix: '', priority: 0 });
  };

  const openEdit = (a: ServiceAreaDTO) => {
    setEditingId(a.id);
    setEditingSeed({ postcodePrefix: a.postcodePrefix, priority: a.priority });
  };

  const cancel = () => setEditingId(null);

  const submit = (values: ServiceAreaFormValues) => {
    setSubmitting(true);
    setNetError(undefined);
    setSaveBanner(undefined);
    const payload = {
      postcodePrefix: String(values.postcodePrefix ?? '').trim().toUpperCase(),
      priority: Number(values.priority) || 0,
    };
    if (!payload.postcodePrefix) {
      setNetError('Postcode prefix is required.');
      setSubmitting(false);
      return;
    }
    const wasNew = editingId === 'new';
    const request = wasNew
      ? axiosKK.post(`/admin/partners/${partnerId}/service-areas`, payload)
      : axiosKK.put(`/admin/partners/${partnerId}/service-areas/${editingId}`, payload);

    request
      .then(() => {
        setEditingId(null);
        setSaveBanner(wasNew ? 'Service area added.' : 'Service area updated.');
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  const remove = (id: number) => {
    setNetError(undefined);
    setSaveBanner(undefined);
    axiosKK
      .delete(`/admin/partners/${partnerId}/service-areas/${id}`)
      .then(() => {
        setSaveBanner('Service area removed.');
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err));
  };

  const addDefault = (postcodePrefix: string, priority: number) => {
    setNetError(undefined);
    setSaveBanner(undefined);
    axiosKK
      .post(`/admin/partners/${partnerId}/service-areas`, { postcodePrefix, priority })
      .then(() => {
        setSaveBanner(`Added ${postcodePrefix}.`);
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err));
  };

  const addAllDefaults = () => {
    setNetError(undefined);
    setSaveBanner(undefined);
    setSubmitting(true);
    Promise.all(
      DEFAULT_SERVICE_AREAS.map((d) =>
        axiosKK.post(`/admin/partners/${partnerId}/service-areas`, {
          postcodePrefix: d.postcodePrefix,
          priority: d.priority,
        })
      )
    )
      .then(() => {
        setSaveBanner(`Added all ${DEFAULT_SERVICE_AREAS.length} suggested service areas.`);
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  const presentPrefixes = new Set(areas.map((a) => a.postcodePrefix.toUpperCase()));
  const suggestedDefaults = DEFAULT_SERVICE_AREAS.filter(
    (d) => !presentPrefixes.has(d.postcodePrefix.toUpperCase())
  );
  const showDefaultsPanel =
    !readOnly && !loading && areas.length === 0 && editingId === null;

  return (
    <section className="admin-partner-service-areas">
      <AdminNotice intent="error">{netError}</AdminNotice>
      <AdminNotice intent="success">{saveBanner}</AdminNotice>

      {loading ? (
        <p>Loading service areas…</p>
      ) : (
        <>
          {showDefaultsPanel && suggestedDefaults.length > 0 && (
            <div className="partner-defaults">
              <h4>Suggested UK 2026 defaults</h4>
              <p className="partner-defaults__hint">
                The 30 most active UK postcode areas for student-storage demand.
                Click a prefix to add it, or add the full set at once. Anything
                you add can be edited or removed afterwards.
              </p>
              <div className="partner-defaults__buttons">
                {suggestedDefaults.map((d) => (
                  <button
                    key={d.postcodePrefix}
                    type="button"
                    className="partner-defaults__button"
                    onClick={() => addDefault(d.postcodePrefix, d.priority)}
                    title={d.region}
                  >
                    + {d.postcodePrefix} <span className="partner-defaults__hint">({d.region})</span>
                  </button>
                ))}
              </div>
              <AddButton onClick={addAllDefaults} disabled={submitting}>
                Add all {suggestedDefaults.length} suggested prefixes
              </AddButton>
            </div>
          )}


          {!readOnly && editingId !== null && (
            <InlineEditor<ServiceAreaFormValues>
              title={editingId === 'new' ? 'New service area' : `Edit area #${editingId}`}
              fields={EDITOR_FIELDS}
              initialValues={editingSeed}
              isSubmitting={submitting}
              onSubmit={submit}
              onCancel={cancel}
            />
          )}

          <DataTable<ServiceAreaDTO>
            columns={[
              { key: 'id', label: 'ID' },
              { key: 'postcodePrefix', label: 'Postcode prefix' },
              { key: 'priority', label: 'Priority' },
            ] as DataTableColumn<ServiceAreaDTO>[]}
            rows={areas}
            keyOf={(a) => a.id}
            emptyMessage="No service areas configured."
            aboveTable={
              readOnly ? undefined : (
                <AddButton onClick={openCreate} disabled={editingId !== null}>
                  Add service area
                </AddButton>
              )
            }
            actionsColumn={
              readOnly
                ? undefined
                : {
                    render: (a) => (
                      <>
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => openEdit(a)}
                          disabled={editingId !== null}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => remove(a.id)}
                        >
                          Delete
                        </button>
                      </>
                    ),
                  }
            }
          />
        </>
      )}
    </section>
  );
}
